import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TourProps } from 'antd';
import { resolveFrontendApiBase } from '../utils/apiBase';
import {
  Card,
  Typography,
  Space,
  Form,
  Input,
  Button,
  Tag,
  Table,
  Tooltip,
  message,
  Empty,
  Spin,
  Modal,
  Radio
} from 'antd';
import type { RadioChangeEvent } from 'antd/es/radio';
import type { ColumnsType } from 'antd/es/table';
import { GiftOutlined, MailOutlined, UserOutlined, ReloadOutlined, CopyOutlined, CheckCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { CustomPricing, Plan, BeforeCheckoutContext, ForeverQuantityChangeEvent } from '../CustomPricing';
import { useAccountContext, GiftPurchase } from '../Account';
import * as fbPixel from '../utils/fbPixel';
import { getFacebookCookies } from '../utils/trackingCookies';
import { getAuthHeaders } from '../utils/authHeaders';
import { getAuth } from 'firebase/auth';
import AppTour, { renderAppTourPanel } from '../tours/AppTour';
import { tourTarget, useGuidedTour } from '../tours/useGuidedTour';
import { ACCOUNT_GIFTS_TOUR_EVENT, ACCOUNT_GIFTS_TOUR_STORAGE_KEY } from './accountTourEvents';
import './AccountGifts.css';

const { Title, Text, Paragraph } = Typography;

const API_BASE = resolveFrontendApiBase();
//const API_BASE = 'http://localhost:8000';

const normalizeInput = (value?: string | null): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const normalizeEmailValue = (value?: string | null): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : undefined;
};

const formatTimestamp = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const statusTag = (status?: string) => {
  const normalized = (status || '').toLowerCase();
  switch (normalized) {
    case 'pending':
      return <Tag color="blue">Pending</Tag>;
    case 'claimed':
      return <Tag color="green">Claimed</Tag>;
    case 'canceled':
    case 'cancelled':
      return <Tag color="red">Cancelled</Tag>;
    default:
      return <Tag>{status || 'Unknown'}</Tag>;
  }
};

const getFacebookTrackingParams = () => {
  const { fbp, fbc } = getFacebookCookies();
  return {
    fbp,
    fbc,
  };
};

type GiftHistoryFilter = 'all' | 'pending' | 'claimed';

interface GiftsSectionProps {
  kicker: string;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  tone?: 'default' | 'info' | 'success';
  step?: number;
}

const GiftsSection: React.FC<GiftsSectionProps> = ({
  kicker,
  title,
  description,
  action,
  children,
  tone = 'default',
  step,
}) => (
  <section className={`account-gifts-section account-gifts-section--${tone}`}>
    <div className="account-gifts-section-header">
      <div className="account-gifts-section-copy">
        <div className="account-gifts-section-eyebrow">
          {step ? <span className="account-gifts-step-badge">{step}</span> : null}
          <span className="account-gifts-kicker">{kicker}</span>
        </div>
        <Title level={3}>{step ? `${step}. ${title}` : title}</Title>
        {description ? <Text type="secondary">{description}</Text> : null}
      </div>
      {action ? <div className="account-gifts-section-action">{action}</div> : null}
    </div>
    <div className="account-gifts-section-body">{children}</div>
  </section>
);

const GiftsModalTitle: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}> = ({ icon, title, subtitle }) => (
  <div className="account-gifts-modal-title">
    <span className="account-gifts-modal-title-icon">{icon}</span>
    <Title level={4}>{title}</Title>
    <Text type="secondary">{subtitle}</Text>
  </div>
);

const confirmRedeem = (giftSummary: {
  credits?: number;
  senderName?: string | null;
  planName?: string | null;
  message?: string | null;
  isRecipientRestricted?: boolean;
  lockedToDifferentAccount?: boolean;
  recipientEmail?: string | null;
}): Promise<boolean> =>
  new Promise((resolve) => {
    import('antd').then(({ Modal }) => {
      const shouldHideDetails = Boolean(giftSummary.lockedToDifferentAccount);
      Modal.confirm({
        rootClassName: 'theme-aware-confirm',
        title: 'Redeem gift credits?',
        icon: <GiftOutlined className="account-gifts-confirm-icon" />,
        content: (
          <div className="gift-confirm-body account-gifts-confirm-body">
            {shouldHideDetails ? (
              <div className="account-gifts-confirm-block account-gifts-confirm-block--warning">
                <Text className="gift-confirm-secondary">
                  Gift details are hidden until you switch to the intended recipient&rsquo;s account.
                </Text>
              </div>
            ) : (
              <>
                <div className="account-gifts-confirm-block account-gifts-confirm-block--success">
                  <span className="account-gifts-kicker">Gift value</span>
                  <Text>
                    <Text strong>{giftSummary.credits ?? 0}</Text> forever credits
                  </Text>
                </div>
                {giftSummary.senderName && (
                  <div className="account-gifts-confirm-block">
                    <span className="account-gifts-kicker">Sender</span>
                    <Text className="gift-confirm-secondary">{giftSummary.senderName}</Text>
                  </div>
                )}
                {giftSummary.message && (
                  <div className="account-gifts-confirm-block">
                    <span className="account-gifts-kicker">Message</span>
                    <Paragraph className="gift-confirm-secondary account-gifts-confirm-message">
                      "{giftSummary.message}"
                    </Paragraph>
                  </div>
                )}
              </>
            )}
            <div className="account-gifts-confirm-block">
              <Text className="gift-confirm-secondary">Redeeming adds the credits to your account immediately.</Text>
            </div>
            {giftSummary.lockedToDifferentAccount ? (
              <div className="account-gifts-confirm-block account-gifts-confirm-block--danger">
                <Text type="danger">
                  This code was earmarked for a different account than the one you&rsquo;re signed into. Redeeming will only succeed if you switch to the intended recipient&rsquo;s account.
                </Text>
              </div>
            ) : giftSummary.isRecipientRestricted ? (
              <div className="account-gifts-confirm-block account-gifts-confirm-block--warning">
                <Text type="warning">
                  This code is earmarked for a specific account. Make sure you&rsquo;re signed into the intended recipient before redeeming.
                </Text>
              </div>
            ) : null}
          </div>
        ),
        okText: 'Redeem Gift',
        cancelText: 'Cancel',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  });

const GiftTokenCell: React.FC<{ token?: string }> = ({ token }) => {
  if (!token) {
    return <Text type="secondary">—</Text>;
  }
  const shortToken = token.length > 12 ? `${token.slice(0, 12)}…` : token;
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(token);
      message.success('Gift code copied to clipboard');
    } catch (_err) {
      message.warning('Copy failed. You can copy the highlighted text manually.');
    }
  };
  return (
    <Space size="small" className="account-gifts-token-cell">
      <Tooltip title={token}>
        <Text code className="account-gifts-token">
          {shortToken}
        </Text>
      </Tooltip>
      <Tooltip title="Copy gift code">
        <Button icon={<CopyOutlined />} size="small" onClick={handleCopy} className="account-gifts-copy-code-button">
          Copy
        </Button>
      </Tooltip>
    </Space>
  );
};

interface CurrentPlanSnapshot {
  planName?: string;
  priceId?: string;
  productId?: string;
  foreverDiscountPercent: number;
  isLegacy: boolean;
  tier?: number;
}

const pickString = (...values: Array<any>): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) {
        return trimmed;
      }
    }
  }
  return undefined;
};

const pickNumber = (...values: Array<any>): number | undefined => {
  for (const value of values) {
    if (value === null || value === undefined) {
      continue;
    }
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
  }
  return undefined;
};

type GiftSplitMode = 'single' | 'multi';

interface GiftCodeEntry {
  recipientEmail?: string;
  recipientName?: string;
  giftMessage?: string;
}

interface PlanGiftPreference {
  planId: string;
  planName?: string;
  creditsPerBundle?: number;
  quantity: number;
  splitMode: GiftSplitMode;
  perCode: GiftCodeEntry[];
  copiedGlobalMessage?: boolean;
}

const clampGiftQuantity = (value?: number): number => {
  if (!Number.isFinite(value)) return 1;
  const numeric = Math.floor(Number(value) || 1);
  if (numeric < 1) return 1;
  if (numeric > 10) return 10;
  return numeric;
};

const resolvePlanPreferenceKey = (plan: Plan): string => {
  return (
    plan.id ||
    plan.stripePriceID ||
    plan.stripeProductID ||
    plan.planName ||
    plan.planKey ||
    'default-plan'
  );
};

const ensureGiftCodeEntries = (
  entries: GiftCodeEntry[],
  quantity: number,
  defaults?: GiftCodeEntry
): GiftCodeEntry[] => {
  const safeQuantity = clampGiftQuantity(quantity);
  const next = entries.slice(0, safeQuantity);
  while (next.length < safeQuantity) {
    next.push({ ...(defaults || {}) });
  }
  return next;
};

const AccountGifts: React.FC = () => {
  const {
    isAuth,
    usersSharedState,
    showLoginModal,
    giftPurchases,
    giftPurchasesLoading,
    refreshGiftPurchases,
    giftPurchasesHasMore,
    giftPurchasesLoadingMore,
    loadMoreGiftPurchases,
    authResolved,
  } = useAccountContext();
  const userID: string | undefined = usersSharedState?.userID;
  const userEmail: string | undefined = usersSharedState?.email;
  const displayName: string = useMemo(
    () => normalizeInput(usersSharedState?.displayName) || normalizeInput(usersSharedState?.name) || '',
    [usersSharedState]
  ) || '';

  const [giftForm] = Form.useForm();
  const [redeemForm] = Form.useForm();
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [deepLinkToken, setDeepLinkToken] = useState<string | null>(null);
  const [loginPrompted, setLoginPrompted] = useState(false);
  const location = useLocation();
  const [planSnapshot, setPlanSnapshot] = useState<CurrentPlanSnapshot>({
    foreverDiscountPercent: 0,
    isLegacy: false,
  });
  const [planLoading, setPlanLoading] = useState(false);
  const [planFetchStatus, setPlanFetchStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [planError, setPlanError] = useState<string | null>(null);
  const [planReloadToken, setPlanReloadToken] = useState(0);
  const [planGiftPreferences, setPlanGiftPreferences] = useState<Record<string, PlanGiftPreference>>({});
  const [activeGiftPlanId, setActiveGiftPlanId] = useState<string | null>(null);
  const [editForm] = Form.useForm();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingGift, setEditingGift] = useState<GiftPurchase | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [giftHistoryFilter, setGiftHistoryFilter] = useState<GiftHistoryFilter>('all');
  const [giftsTourPendingStart, setGiftsTourPendingStart] = useState(false);
  const canInitiateGiftPurchase = Boolean(isAuth && userID);
  const redeemSectionRef = useRef<HTMLDivElement | null>(null);
  const redeemReady = Boolean(authResolved && isAuth && userID);
  const activePlanPreference = useMemo(
    () => (activeGiftPlanId ? planGiftPreferences[activeGiftPlanId] || null : null),
    [activeGiftPlanId, planGiftPreferences]
  );
  const showPerCodeConfigurator = false;
  const disableGlobalRecipientFields = false;
  const retryPlanSnapshot = useCallback(() => {
    setPlanReloadToken((token) => token + 1);
  }, []);

  const focusRedeemCode = useCallback(() => {
    redeemSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => {
      const tokenInput =
        redeemForm.getFieldInstance?.('token') ||
        redeemSectionRef.current?.querySelector<HTMLInputElement>('input');
      if (tokenInput && typeof tokenInput.focus === 'function') {
        tokenInput.focus();
      }
    }, 180);
  }, [redeemForm]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('gift') !== 'success') {
      return;
    }
    message.success('Gift purchase completed! Share your gift code with the recipient.');
    refreshGiftPurchases();

    const maxAttempts = 4;
    const refreshIntervalMs = 2000;
    let attempts = 0;
    let intervalId: number | null = null;

    if (typeof window !== 'undefined') {
      intervalId = window.setInterval(() => {
        attempts += 1;
        refreshGiftPurchases();
        if (attempts >= maxAttempts && intervalId !== null) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
      }, refreshIntervalMs);

      ['gift', 'session_id', 'plan'].forEach((key) => params.delete(key));
      const qs = params.toString();
      const next = `${location.pathname}${qs ? `?${qs}` : ''}${location.hash || ''}`;
      window.history.replaceState(null, '', next);
    }

    return () => {
      if (intervalId !== null && typeof window !== 'undefined') {
        window.clearInterval(intervalId);
      }
    };
  }, [location, refreshGiftPurchases]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = normalizeInput(params.get('token'));
    setDeepLinkToken((prev) => {
      const next = tokenParam || null;
      if (prev !== next) {
        setLoginPrompted(false);
      }
      return next;
    });
  }, [location.search]);

  useEffect(() => {
    if (!deepLinkToken) {
      return;
    }
    const currentValue = normalizeInput(redeemForm.getFieldValue('token'));
    if (currentValue === deepLinkToken) {
      return;
    }
    redeemForm.setFieldsValue({ token: deepLinkToken });
    if (redeemSectionRef.current) {
      redeemSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    const fieldInstance = redeemForm.getFieldInstance ? redeemForm.getFieldInstance('token') : null;
    if (fieldInstance && typeof fieldInstance.focus === 'function') {
      try {
        fieldInstance.focus();
      } catch (_err) {
        // ignore focus errors
      }
    }
  }, [deepLinkToken, redeemForm, isAuth, userID]);

  useEffect(() => {
    if (!deepLinkToken || loginPrompted || !authResolved) {
      return;
    }
    if (isAuth) {
      if (!userID) {
        return;
      }
      return;
    }
    const auth = getAuth();
    if (auth.currentUser) {
      return;
    }
    showLoginModal();
    setLoginPrompted(true);
  }, [deepLinkToken, loginPrompted, authResolved, isAuth, userID, showLoginModal]);

useEffect(() => {
  giftForm.resetFields();
}, [giftForm, userID, isAuth]);

useEffect(() => {
  if (displayName) {
    const existing = giftForm.getFieldValue('senderName');
    if (!existing) {
      giftForm.setFieldsValue({ senderName: displayName });
    }
  }
}, [displayName, giftForm]);

useEffect(() => {
  if (!isAuth || !userID) {
    setPlanSnapshot({ foreverDiscountPercent: 0, isLegacy: false });
    setPlanLoading(false);
    setPlanFetchStatus('idle');
    setPlanError(null);
    return;
  }
  let cancelled = false;
  const controller = new AbortController();
  const safeSetPlanSnapshot = (next: CurrentPlanSnapshot) => {
    if (!cancelled) {
      setPlanSnapshot(next);
    }
  };

  const fetchPlanSnapshot = async () => {
    setPlanLoading(true);
    setPlanFetchStatus('loading');
    setPlanError(null);
    try {
      let headers: Record<string, string> | undefined;
      try {
        headers = await getAuthHeaders();
      } catch (_authError) {
        safeSetPlanSnapshot({ foreverDiscountPercent: 0, isLegacy: false });
        if (!cancelled) {
          setPlanFetchStatus('error');
          setPlanError('We could not verify your account. Please sign in again and retry.');
        }
        return;
      }
      const { data } = await axios.get(`${API_BASE}/get-subscription-details`, {
        params: { userID },
        signal: controller.signal,
        headers,
      });
      if (cancelled) return;

      let planName = pickString(
        data?.plan_name,
        data?.planName,
        data?.plan?.planName,
        data?.plan?.displayName,
        data?.subscription?.plan?.nickname,
        data?.subscription?.plan?.name
      );

      let priceId = pickString(
        data?.stripe_price_id,
        data?.price_id,
        data?.plan_price_id,
        data?.plan?.stripePriceID,
        data?.plan?.stripePriceId,
        data?.plan?.priceId,
        data?.subscription?.plan?.id,
        data?.subscription?.items?.data?.[0]?.price?.id
      );

      let productId = pickString(
        data?.plan_product_id,
        data?.product_id,
        data?.plan?.stripeProductID,
        data?.plan?.stripeProductId,
        data?.plan?.productId,
        data?.plan?.planId,
        data?.subscription?.plan?.product
      );

      let tier = pickNumber(
        data?.plan_tier,
        data?.planTier,
        data?.plan?.tier
      );

      let discountPercent = pickNumber(
        data?.forever_credit_discount_percent,
        data?.foreverCreditDiscountPercent,
        data?.plan?.foreverCreditDiscountPercent,
        data?.plan?.metadata?.foreverCreditDiscountPercent,
        data?.plan?.metadata?.foreverCreditsDiscount
      );

      let isLegacy = Boolean(
        data?.plan_is_legacy ??
        data?.planIsLegacy ??
        data?.plan?.isLegacy
      );

      // Fallback to plan lookup for richer metadata
      const lookupId = priceId || productId;
      if (lookupId) {
        try {
          const params = priceId ? { priceId } : undefined;
          const planResponse = await axios.get(`${API_BASE}/get_plan_details/${lookupId}`, {
            params,
            signal: controller.signal,
          });
          const planData = planResponse.data || {};
          planName = planName || pickString(planData?.displayName, planData?.planName);
          priceId = priceId || pickString(planData?.stripePriceID, planData?.stripePriceId, planData?.priceId);
          productId = productId || pickString(planData?.stripeProductID, planData?.stripeProductId, planData?.productId);
          tier = tier ?? pickNumber(planData?.tier);
          discountPercent = discountPercent ?? pickNumber(
            planData?.foreverCreditDiscountPercent,
            planData?.metadata?.foreverCreditDiscountPercent,
            planData?.metadata?.foreverCreditsDiscount
          );
          if (typeof planData?.isLegacy === 'boolean') {
            isLegacy = planData.isLegacy;
          }
        } catch (planError) {
          if (!axios.isCancel(planError)) {
            console.warn('[gifts] failed to resolve plan details', planError);
          }
        }
      }

      if (cancelled) return;

      safeSetPlanSnapshot({
        planName,
        priceId,
        productId,
        foreverDiscountPercent: discountPercent !== undefined ? Math.max(0, discountPercent) : 0,
        isLegacy,
        tier: tier !== undefined ? tier : undefined,
      });
      if (!cancelled) {
        setPlanFetchStatus('success');
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.warn('[gifts] failed to load plan snapshot', error);
        safeSetPlanSnapshot({ foreverDiscountPercent: 0, isLegacy: false });
        if (!cancelled) {
          const status: number | undefined = (error as any)?.response?.status;
          const detailRaw = (error as any)?.response?.data?.detail;
          const detailText = typeof detailRaw === 'string' ? detailRaw : '';
          const isMissingStripeCustomer =
            status === 400 &&
            (detailText.includes('does not have a Stripe customer ID') ||
              detailText.includes('No subscriptions found') ||
              detailText.includes('No renewable subscription'));
          if (isMissingStripeCustomer) {
            setPlanFetchStatus('success');
            setPlanError(null);
          } else {
            setPlanFetchStatus('error');
            setPlanError('We couldn’t load your plan details. Please try again.');
          }
        }
      }
    } finally {
      if (!cancelled) {
        setPlanLoading(false);
      }
    }
  };

  fetchPlanSnapshot();

  return () => {
    cancelled = true;
    controller.abort();
  };
}, [isAuth, userID, planReloadToken]);

  const handleGiftPlanQuantityChange = useCallback(
    (event: ForeverQuantityChangeEvent) => {
      if (!event || !event.plan) {
        return;
      }
      const planId = event.planId || resolvePlanPreferenceKey(event.plan);
      const nextQuantity = clampGiftQuantity(event.quantity);
      const planName =
        event.plan.planName ||
        (event.plan as any)?.displayName ||
        event.plan.planKey ||
        'Gift bundle';
      const creditsPerBundle =
        typeof event.plan.oneTimeCredits === 'number'
          ? event.plan.oneTimeCredits
          : Number((event.plan as any)?.foreverMeta?.credits ?? 0) || undefined;
      const defaultDraft: GiftCodeEntry = {
        giftMessage: giftForm.getFieldValue('giftMessage') || undefined,
      };
      setPlanGiftPreferences((prev) => {
        const existing = prev[planId];
        const nextEntry: PlanGiftPreference = {
          planId,
          planName,
          creditsPerBundle,
          quantity: nextQuantity,
          splitMode: existing?.splitMode || 'single',
          perCode: ensureGiftCodeEntries(existing?.perCode || [], nextQuantity, defaultDraft),
          copiedGlobalMessage: existing?.copiedGlobalMessage || false,
        };
        return { ...prev, [planId]: nextEntry };
      });
      setActiveGiftPlanId(planId);
    },
    [giftForm]
  );

  const handleSplitModeChange = useCallback(
    (planId: string, mode: GiftSplitMode) => {
      setPlanGiftPreferences((prev) => {
        const existing = prev[planId];
        if (!existing || existing.splitMode === mode) {
          return prev;
        }
        let nextEntries = ensureGiftCodeEntries(existing.perCode, existing.quantity);
        let copiedGlobalMessage = existing.copiedGlobalMessage;
        if (mode === 'multi' && !copiedGlobalMessage) {
          const globalMessage = normalizeInput(giftForm.getFieldValue('giftMessage'));
          const globalEmail = normalizeEmailValue(giftForm.getFieldValue('recipientEmail'));
          const globalName = normalizeInput(giftForm.getFieldValue('recipientName'));
          if (globalMessage || globalEmail || globalName) {
            nextEntries = nextEntries.map((entry, index) => {
              if (index === 0) {
                return {
                  ...entry,
                  recipientEmail: entry.recipientEmail || globalEmail || entry.recipientEmail,
                  recipientName: entry.recipientName || globalName || entry.recipientName,
                  giftMessage: entry.giftMessage || globalMessage || entry.giftMessage,
                };
              }
              return {
                ...entry,
                giftMessage: entry.giftMessage || globalMessage || entry.giftMessage,
              };
            });
            copiedGlobalMessage = true;
          }
        }
        return {
          ...prev,
          [planId]: {
            ...existing,
            splitMode: mode,
            perCode: nextEntries,
            copiedGlobalMessage,
          },
        };
      });
    },
    [giftForm]
  );

  const handlePerCodeEntryChange = useCallback(
    (planId: string, index: number, field: keyof GiftCodeEntry, value: string) => {
      setPlanGiftPreferences((prev) => {
        const existing = prev[planId];
        if (!existing || index < 0 || index >= existing.perCode.length) {
          return prev;
        }
        const entries = existing.perCode.slice();
        entries[index] = {
          ...entries[index],
          [field]: value,
        };
        return {
          ...prev,
          [planId]: {
            ...existing,
            perCode: entries,
          },
        };
      });
    },
    []
  );

  const openEditGiftModal = useCallback(
    (gift: GiftPurchase) => {
      setEditingGift(gift);
      editForm.setFieldsValue({
        senderName: gift.senderName || '',
        recipientEmail: gift.recipientEmail || '',
        recipientName: gift.recipientName || '',
        giftMessage: gift.message || '',
      });
      setEditModalVisible(true);
    },
    [editForm]
  );

  const closeEditGiftModal = useCallback(() => {
    setEditModalVisible(false);
    setEditingGift(null);
    editForm.resetFields();
  }, [editForm]);

  const handleEditSubmit = useCallback(async () => {
    if (!editingGift) {
      return;
    }
    try {
      const values = await editForm.validateFields();
      setEditSaving(true);
      const headers = await getAuthHeaders();
      const encodeField = (value?: string) => {
        if (value === undefined || value === null) return undefined;
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : '';
      };
      const payload: Record<string, any> = { giftId: editingGift.id };
      const encodedRecipientEmail = encodeField(values.recipientEmail);
      const encodedRecipientName = encodeField(values.recipientName);
      const encodedGiftMessage = encodeField(values.giftMessage);
      if (encodedRecipientEmail !== undefined) {
        payload.recipientEmail = encodedRecipientEmail;
      }
      if (encodedRecipientName !== undefined) {
        payload.recipientName = encodedRecipientName;
      }
      if (encodedGiftMessage !== undefined) {
        payload.giftMessage = encodedGiftMessage;
      }
      if (values.senderName !== undefined) {
        payload.senderName = values.senderName.trim();
      }
      await axios.post(`${API_BASE}/gifts/update`, payload, { headers });
      message.success('Gift details updated.');
      closeEditGiftModal();
      refreshGiftPurchases();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (detail) {
        message.error(detail);
      } else if (error?.name === 'Error') {
        // form validation errors already shown
      } else {
        message.error('Unable to update gift. Please try again.');
      }
    } finally {
      setEditSaving(false);
    }
  }, [editingGift, editForm, refreshGiftPurchases, closeEditGiftModal]);

  const beforeCheckout = useCallback(async (plan: Plan, context?: BeforeCheckoutContext) => {
    if (!userID) {
      message.error('Please sign in again before buying gift credits.');
      showLoginModal();
      return false;
    }
    try {
      const values = await giftForm.validateFields();
      const recipientEmail = normalizeEmailValue(values.recipientEmail);
      const payloadRecipientName = normalizeInput(values.recipientName);
      const payloadSenderName = normalizeInput(values.senderName) || normalizeInput(displayName);
      const payloadMessage = normalizeInput(values.giftMessage);
      const normalizedQuantity = clampGiftQuantity(context?.quantity);
      const planPreferenceKey = resolvePlanPreferenceKey(plan);

      const trackingParams = getFacebookTrackingParams();

      const overrides = {
        metadata: {
          gift_intent: 'true',
          ...(recipientEmail ? { gift_recipient_email: recipientEmail } : {}),
          ...(payloadRecipientName ? { gift_recipient_name: payloadRecipientName } : {}),
          ...(payloadSenderName ? { gift_sender_name: payloadSenderName } : {}),
          ...(payloadMessage ? { gift_message: payloadMessage } : {}),
          ...(normalizedQuantity > 1 ? { gift_split_mode: 'single' } : {}),
        },
        payload: {
          successUrl: `${window.location.origin}/account/gifts?gift=success`,
          isGiftPurchase: true,
          giftRecipientEmail: recipientEmail,
          giftRecipientName: payloadRecipientName,
          giftSenderName: payloadSenderName,
          giftMessage: payloadMessage,
          fbp: trackingParams.fbp ?? undefined,
          fbc: trackingParams.fbc ?? undefined,
          ...(normalizedQuantity > 1 ? { giftSplitMode: 'single' as const } : {}),
        },
      };

      return overrides;
    } catch (_validationError) {
      // AntD form will display validation feedback automatically.
      return false;
    }
  }, [giftForm, displayName, userID, showLoginModal, planGiftPreferences]);

  const handleRedeem = useCallback(
    async (values: { token: string }) => {
      if (redeemLoading) {
        return;
      }
      if (!userID) {
        showLoginModal();
        return;
      }
      const trimmedToken = normalizeInput(values.token);
      if (!trimmedToken) {
        message.error('Enter a gift code to redeem.');
        return;
      }
      setRedeemLoading(true);
      let giftSummary: any = null;
      const resolveAuthHeaders = async () => {
        try {
          return await getAuthHeaders();
        } catch (_authError) {
          message.error('Please sign in again to redeem this gift.');
          showLoginModal();
          throw _authError;
        }
      };
      let lookupHeaders: Record<string, string>;
      try {
        lookupHeaders = await resolveAuthHeaders();
      } catch (_authError) {
        setRedeemLoading(false);
        return;
      }
      try {
        const lookupResponse = await axios.get(`${API_BASE}/gifts/lookup`, {
          params: { token: trimmedToken },
          headers: lookupHeaders,
        });
        giftSummary = lookupResponse.data?.gift;
        if (!giftSummary) {
          message.error('Gift not found or unavailable.');
          setRedeemLoading(false);
          return;
        }
      } catch (error: any) {
        setRedeemLoading(false);
        const detail = error?.response?.data?.detail;
        message.error(typeof detail === 'string' ? detail : 'Failed to find gift. Check the code and try again.');
        return;
      }

      const normalizedStatus =
        typeof giftSummary.status === 'string' ? giftSummary.status.toLowerCase() : 'pending';
      if (normalizedStatus !== 'pending') {
        if (normalizedStatus === 'claimed') {
          const claimedOn = formatTimestamp(giftSummary.claimedAt);
          message.error(
            claimedOn && claimedOn !== '—'
              ? `This gift code was already claimed on ${claimedOn}.`
              : 'This gift code was already claimed.'
          );
        } else if (normalizedStatus === 'canceled' || normalizedStatus === 'cancelled') {
          message.error('This gift was canceled and can no longer be redeemed.');
        } else {
          message.error('This gift is no longer available.');
        }
        setRedeemLoading(false);
        return;
      }
      const intendedEmail = normalizeEmailValue(giftSummary.recipientEmail);
      const currentUserEmail = normalizeEmailValue(userEmail);
      const lockedToDifferentAccount =
        Boolean(intendedEmail && currentUserEmail && intendedEmail !== currentUserEmail);
      const confirmed = await confirmRedeem({
        credits: giftSummary.credits,
        senderName: giftSummary.senderName,
        planName: giftSummary.planName,
        message: giftSummary.message,
        isRecipientRestricted: Boolean(intendedEmail),
        lockedToDifferentAccount,
        recipientEmail: giftSummary.recipientEmail,
      });
      if (!confirmed) {
        setRedeemLoading(false);
        return;
      }

      let claimHeaders: Record<string, string>;
      try {
        claimHeaders = await resolveAuthHeaders();
      } catch (_authError) {
        setRedeemLoading(false);
        return;
      }
      try {
        await axios.post(`${API_BASE}/gifts/claim`, {
          userID,
          token: trimmedToken,
        }, { headers: claimHeaders });
        message.success('Gift redeemed! Credits have been added to your account.');
        redeemForm.resetFields();
        refreshGiftPurchases();
        const purchaseValue = typeof giftSummary.amountTotal === 'number' ? giftSummary.amountTotal : null;
        const rawCurrency = typeof giftSummary.currency === 'string' ? giftSummary.currency : '';
        const purchaseCurrency = rawCurrency.trim() ? rawCurrency.trim().toUpperCase() : 'USD';
        const pixelPayload: Record<string, any> = {
          content_name: 'gift_claim',
          gift_credits: giftSummary.credits ?? 0,
        };
        if (purchaseValue !== null) {
          pixelPayload.purchase_value = purchaseValue;
          pixelPayload.purchase_currency = purchaseCurrency;
        }
        fbPixel.event('GiftClaim', pixelPayload, fbPixel.generateEventId('GiftClaim'));
      } catch (error: any) {
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail;
        const detailText = typeof detail === 'string' ? detail : '';
        const indicatesDifferentAccount =
          status === 403 && detailText.toLowerCase().includes('different recipient email');
        if (indicatesDifferentAccount) {
          message.error('This code is earmarked for a different account. Sign into that account or check with the sender.');
        } else if (status === 401 || status === 403) {
          message.error('Please sign in again to redeem this gift.');
          showLoginModal();
        } else {
          message.error(detailText || 'Failed to redeem gift.');
        }
      } finally {
        setRedeemLoading(false);
      }
    },
    [redeemLoading, userID, redeemForm, refreshGiftPurchases, showLoginModal, userEmail]
  );

  const filteredGiftPurchases = useMemo(
    () =>
      giftPurchases.filter((gift) => {
        if (giftHistoryFilter === 'all') {
          return true;
        }
        return (gift.status || '').toLowerCase() === giftHistoryFilter;
      }),
    [giftHistoryFilter, giftPurchases]
  );

  const giftColumns: ColumnsType<GiftPurchase> = useMemo(
    () => [
      {
        title: 'Recipient',
        dataIndex: 'recipientEmail',
        key: 'recipientEmail',
        render: (_: unknown, record) => record.recipientEmail || 'Any recipient',
        width: 220,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (value: string | undefined) => statusTag(value),
        width: 120,
      },
      {
        title: 'Gift Code',
        dataIndex: 'token',
        key: 'token',
        render: (value: string | undefined, record) =>
          record.status === 'pending' ? <GiftTokenCell token={value} /> : <Text type="secondary">—</Text>,
        width: 230,
      },
      {
        title: 'Credits',
        dataIndex: 'credits',
        key: 'credits',
        render: (value: number | undefined) => (value ?? 0).toLocaleString(),
        width: 100,
      },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (value: string | null | undefined) => formatTimestamp(value),
        width: 190,
      },
      {
        title: 'Claimed',
        dataIndex: 'claimedAt',
        key: 'claimedAt',
        render: (value: string | null | undefined, record) =>
          record.status === 'claimed' ? (
            <Space direction="vertical" size={0}>
              <Text>{formatTimestamp(value)}</Text>
            </Space>
          ) : (
            <Text type="secondary">—</Text>
          ),
        width: 190,
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_value, record) =>
          record.status === 'pending' ? (
            <Button size="small" onClick={() => openEditGiftModal(record)}>
              Edit details
            </Button>
          ) : (
            <Text type="secondary">—</Text>
          ),
        width: 130,
      },
    ],
    [openEditGiftModal]
  );
  const giftsTour = useGuidedTour({
    userId: userID,
    apiBase: API_BASE,
    flag: 'accountGiftsTourSeen',
    enabled: Boolean(authResolved),
    autoStart: true,
    ready: authResolved,
  });
  const startGiftsTour = giftsTour.start;
  const giftsTourSteps = useMemo<TourProps['steps']>(() => ([
    {
      title: 'Start with gift credits',
      description: renderAppTourPanel('Buy transferable forever-credit bundles, share generated codes, or redeem a code someone sent you.'),
      target: tourTarget('account-gifts-header'),
    },
    {
      title: 'Add recipient details',
      description: renderAppTourPanel('Recipient fields are optional unless you want to lock a gift code to a specific email.'),
      target: tourTarget('account-gifts-recipient'),
    },
    {
      title: 'Choose a credit pack',
      description: renderAppTourPanel('Gift purchases use forever-credit checkout and inherit the account pricing available to you.'),
      target: tourTarget('account-gifts-pricing'),
    },
    {
      title: 'Configure gift codes',
      description: renderAppTourPanel('When buying multiple bundles, choose one combined code or separate codes with per-recipient notes.'),
      target: tourTarget('account-gifts-code-options', 'account-gifts-pricing'),
    },
    {
      title: 'Redeem received codes',
      description: renderAppTourPanel('Paste a gift code here to add its forever credits to this account.'),
      target: tourTarget('account-gifts-redeem'),
    },
    {
      title: 'Manage issued codes',
      description: renderAppTourPanel('The history table keeps pending and claimed gift codes available for sharing, filtering, and editing.'),
      target: tourTarget('account-gifts-history'),
    },
  ]), []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const pending = window.sessionStorage.getItem(ACCOUNT_GIFTS_TOUR_STORAGE_KEY);
    if (pending) {
      window.sessionStorage.removeItem(ACCOUNT_GIFTS_TOUR_STORAGE_KEY);
      setGiftsTourPendingStart(true);
    }
    const handleTourRequest = () => {
      setGiftsTourPendingStart(true);
    };
    window.addEventListener(ACCOUNT_GIFTS_TOUR_EVENT, handleTourRequest as EventListener);
    return () => {
      window.removeEventListener(ACCOUNT_GIFTS_TOUR_EVENT, handleTourRequest as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!giftsTourPendingStart || !authResolved) return;
    startGiftsTour();
    setGiftsTourPendingStart(false);
  }, [authResolved, giftsTourPendingStart, startGiftsTour]);

  return (
    <div className="account-gifts-shell">
      <Card
        bordered
        className="account-gifts-card"
        title={
          <div className="account-gifts-card-title" data-tour-id="account-gifts-header">
            <div className="account-gifts-card-heading">
              <span className="account-gifts-card-icon"><GiftOutlined /></span>
              <div className="account-gifts-card-copy">
                <span className="account-gifts-kicker">Gifts</span>
                <Title level={2}>Gift Credits</Title>
                <Text type="secondary">Buy transferable forever-credit bundles, share codes, and redeem gifts.</Text>
              </div>
            </div>
            <Button
              icon={<CheckCircleOutlined />}
              onClick={focusRedeemCode}
              className="account-gifts-inline-action"
            >
              Redeem code
            </Button>
          </div>
        }
      >
        <div className="account-gifts-send-grid">
          <GiftsSection
            kicker="Recipient"
            title="Recipient details"
            description="We generate a shareable gift code after checkout."
            tone="info"
            step={1}
          >
            <Form
              data-tour-id="account-gifts-recipient"
              layout="vertical"
              form={giftForm}
              initialValues={{ senderName: displayName }}
              className="account-gifts-form"
            >
              <Form.Item
                name="senderName"
                label="Your name"
                rules={[
                  { required: true, message: 'Please enter your name' },
                  { max: 120, message: 'Name must be 120 characters or fewer' },
                ]}
              >
                <Input prefix={<UserOutlined />} placeholder="Your name" maxLength={120} />
              </Form.Item>
              <Form.Item
                name="recipientEmail"
                label="Recipient email (optional)"
                extra="Optional. If set, only that email can redeem this gift."
                rules={[
                  { type: 'email', message: 'Enter a valid email address' },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="friend@example.com"
                  maxLength={160}
                  disabled={disableGlobalRecipientFields}
                />
              </Form.Item>
              <Form.Item
                name="recipientName"
                label="Recipient name (optional)"
                rules={[{ max: 120, message: 'Name must be 120 characters or fewer' }]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Recipient name"
                  maxLength={120}
                  disabled={disableGlobalRecipientFields}
                />
              </Form.Item>
              <Form.Item
                name="giftMessage"
                label="Message (optional)"
                extra="Included with the gift details when shared or redeemed."
                rules={[{ max: 500, message: 'Message must be 500 characters or fewer' }]}
              >
                <Input.TextArea
                  placeholder="Add a note to include with the gift"
                  maxLength={500}
                  showCount
                  autoSize={{ minRows: 3, maxRows: 4 }}
                  disabled={disableGlobalRecipientFields}
                />
              </Form.Item>
            </Form>
          </GiftsSection>

          <div className="account-gifts-purchase-stack">
            <GiftsSection
              kicker="Checkout"
              title="Choose a credit pack"
              description="Gift purchases use the same forever-credit checkout with your account pricing."
              tone="success"
              step={2}
              action={
                planFetchStatus === 'success' && planSnapshot.planName ? (
                  <span className="account-gifts-plan-pill">Current plan: {planSnapshot.planName}</span>
                ) : planLoading ? (
                  <span className="account-gifts-plan-pill account-gifts-plan-pill--muted">Loading plan</span>
                ) : null
              }
            >
              {planFetchStatus === 'success' && planSnapshot.foreverDiscountPercent <= 0 && (
                <Text type="secondary" className="account-gifts-plan-note">
                  Gifts are purchased at the standard forever-credit price for your account.
                </Text>
              )}

              <div className="account-gifts-pricing-wrap" data-tour-id="account-gifts-pricing">
                {canInitiateGiftPurchase ? (
                  planFetchStatus === 'success' ? (
                    <CustomPricing
                      userID={userID as string}
                      email={userEmail}
                      isAuth={isAuth}
                      showLoginModal={showLoginModal}
                      isForeverCredits
                      enableForeverQuantitySelection
                      renderMode="cardsOnly"
                      purchaseMode="redirect"
                      compactForeverGrid
                      currentPlanPriceId={planSnapshot.priceId}
                      currentPlanForeverDiscountPercent={planSnapshot.foreverDiscountPercent}
                      currentPlanIsLegacy={planSnapshot.isLegacy}
                      currentPlanTier={planSnapshot.tier}
                      beforeCheckout={beforeCheckout}
                      onForeverQuantityChange={handleGiftPlanQuantityChange}
                    />
                  ) : planFetchStatus === 'error' ? (
                    <div className="account-gifts-centered-state">
                      <Text type="danger">{planError || 'Unable to load your plan details.'}</Text>
                      <Button icon={<ReloadOutlined />} onClick={retryPlanSnapshot} className="account-gifts-inline-action">
                        Retry loading plan
                      </Button>
                    </div>
                  ) : (
                    <div className="account-gifts-centered-state">
                      <Spin />
                      <Text type="secondary">Loading your pricing options...</Text>
                    </div>
                  )
                ) : isAuth ? (
                  <div className="account-gifts-centered-state">
                    <Spin />
                    <Text type="secondary">Loading your account before starting checkout...</Text>
                  </div>
                ) : (
                  <div className="account-gifts-centered-state">
                    <Text type="secondary">Sign in to buy gift credits.</Text>
                    <Button type="primary" onClick={showLoginModal} className="account-gifts-primary-action">
                      Sign in to continue
                    </Button>
                  </div>
                )}
              </div>
            </GiftsSection>

            {showPerCodeConfigurator && activePlanPreference && (
              <GiftsSection
                kicker="Advanced"
                title="Gift code options"
                description="Configure whether selected bundles produce one code or multiple codes."
              >
                <div className="account-gifts-code-options" data-tour-id="account-gifts-code-options">
                  <Text>
                    You selected {activePlanPreference.quantity}{' '}
                    {activePlanPreference.quantity === 1 ? 'bundle' : 'bundles'} for{' '}
                    {activePlanPreference.planName || 'this credit pack'}.
                  </Text>
                  <Radio.Group
                    value={activePlanPreference.splitMode}
                    onChange={(event: RadioChangeEvent) =>
                      handleSplitModeChange(activePlanPreference.planId, event.target.value as GiftSplitMode)
                    }
                  >
                    <Radio value="single">
                      One code containing all credits
                      {activePlanPreference.creditsPerBundle
                        ? ` (${activePlanPreference.creditsPerBundle * activePlanPreference.quantity} total)`
                        : ''}
                    </Radio>
                    <Radio value="multi">
                      {activePlanPreference.quantity}{' '}
                      {activePlanPreference.quantity === 1 ? 'separate code' : 'separate codes'} (one per bundle)
                    </Radio>
                  </Radio.Group>
                  {activePlanPreference.splitMode === 'multi' && (
                    <div className="account-gifts-per-code-list">
                      <Text type="secondary">
                        Add recipient details for each gift code (optional). Leave a field blank to keep that code
                        unassigned.
                      </Text>
                      {activePlanPreference.perCode.map((entry, index) => (
                        <Card key={`${activePlanPreference.planId}-${index}`} type="inner" className="account-gifts-per-code-card">
                          <Space direction="vertical" size="small" className="account-gifts-full-width">
                            <Text strong>Gift #{index + 1}</Text>
                            <Input
                              placeholder="Recipient email (optional)"
                              value={entry.recipientEmail || ''}
                              onChange={(e) =>
                                handlePerCodeEntryChange(
                                  activePlanPreference.planId,
                                  index,
                                  'recipientEmail',
                                  e.target.value
                                )
                              }
                            />
                            <Input
                              placeholder="Recipient name (optional)"
                              value={entry.recipientName || ''}
                              onChange={(e) =>
                                handlePerCodeEntryChange(
                                  activePlanPreference.planId,
                                  index,
                                  'recipientName',
                                  e.target.value
                                )
                              }
                            />
                            <Input.TextArea
                              placeholder="Personal message (optional)"
                              value={entry.giftMessage || ''}
                              maxLength={500}
                              showCount
                              autoSize={{ minRows: 2, maxRows: 3 }}
                              onChange={(e) =>
                                handlePerCodeEntryChange(
                                  activePlanPreference.planId,
                                  index,
                                  'giftMessage',
                                  e.target.value
                                )
                              }
                            />
                          </Space>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </GiftsSection>
            )}
          </div>
        </div>
      </Card>

      <div ref={redeemSectionRef} data-tour-id="account-gifts-redeem">
        <GiftsSection
          kicker="Redeem"
          title="Redeem a Gift"
          description="Apply a gift code to add forever credits to your account."
          tone="info"
        >
          {!redeemReady && (
            <div className="account-gifts-redeem-status">
              <Spin size="small" />
              <Text type="secondary">Checking your account...</Text>
            </div>
          )}
          <Form layout="inline" form={redeemForm} onFinish={handleRedeem} className="account-gifts-redeem-form">
            <Form.Item
              name="token"
              className="account-gifts-redeem-token"
              rules={[{ required: true, message: 'Enter your gift code' }]}
            >
              <Input placeholder="Enter gift code" />
            </Form.Item>
            <Form.Item className="account-gifts-redeem-submit">
              <Button type="primary" htmlType="submit" loading={redeemLoading} disabled={!redeemReady}>
                Redeem Credits
              </Button>
            </Form.Item>
          </Form>
          <div className="account-gifts-redeem-helper">
            <Text type="secondary">
              Gift codes never expire. If a code was earmarked for a different email, it must be redeemed by that account.
            </Text>
          </div>
        </GiftsSection>
      </div>

      <div data-tour-id="account-gifts-history">
      <GiftsSection
        kicker="History"
        title="Your Gift Codes"
        description="Issued gift codes remain available here for sharing and support."
        action={
          <div className="account-gifts-history-actions">
            <div className="account-gifts-filter-pills" role="group" aria-label="Gift code status filter">
              <Button
                size="small"
                type={giftHistoryFilter === 'all' ? 'primary' : 'default'}
                onClick={() => setGiftHistoryFilter('all')}
              >
                All
              </Button>
              <Button
                size="small"
                type={giftHistoryFilter === 'pending' ? 'primary' : 'default'}
                onClick={() => setGiftHistoryFilter('pending')}
              >
                Pending
              </Button>
              <Button
                size="small"
                type={giftHistoryFilter === 'claimed' ? 'primary' : 'default'}
                onClick={() => setGiftHistoryFilter('claimed')}
              >
                Claimed
              </Button>
            </div>
            <Button
              icon={<ReloadOutlined />}
              onClick={refreshGiftPurchases}
              loading={giftPurchasesLoading}
              className="account-gifts-inline-action"
            >
              Refresh
            </Button>
          </div>
        }
      >
        <div className="account-gifts-table-wrap">
          <Table<GiftPurchase>
            dataSource={filteredGiftPurchases}
            columns={giftColumns}
            loading={giftPurchasesLoading}
            rowKey={(record) => record.id}
            pagination={false}
            locale={{
              emptyText: (
                <Empty
                  description={
                    giftPurchases.length
                      ? 'No gift codes match this filter.'
                      : 'No gift codes yet. Gift a credit bundle to see it here.'
                  }
                />
              ),
            }}
            scroll={{ x: true }}
          />
        </div>
        {giftPurchasesHasMore && (
          <div className="account-gifts-load-more">
            <Button onClick={loadMoreGiftPurchases} loading={giftPurchasesLoadingMore} disabled={giftPurchasesLoading}>
              Load More
            </Button>
          </div>
        )}
      </GiftsSection>
      </div>

      <Modal
        title={
          <GiftsModalTitle
            icon={<GiftOutlined />}
            title="Edit gift details"
            subtitle="Update the sender, recipient, or note for an unclaimed gift."
          />
        }
        open={editModalVisible}
        onCancel={closeEditGiftModal}
        onOk={() => editForm.submit()}
        confirmLoading={editSaving}
        destroyOnClose
        className="account-gifts-modal"
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditSubmit} className="account-gifts-modal-form">
          <Form.Item
            name="senderName"
            label="Sender name (optional)"
            rules={[{ max: 120, message: 'Name must be 120 characters or fewer' }]}
          >
            <Input placeholder="Shown to recipient" maxLength={120} />
          </Form.Item>
          <Form.Item
            name="recipientEmail"
            label="Recipient email (optional)"
            rules={[{ type: 'email', message: 'Enter a valid email address' }]}
          >
            <Input placeholder="friend@example.com" maxLength={160} />
          </Form.Item>
          <Form.Item
            name="recipientName"
            label="Recipient name (optional)"
            rules={[{ max: 120, message: 'Name must be 120 characters or fewer' }]}
          >
            <Input placeholder="Recipient name" maxLength={120} />
          </Form.Item>
          <Form.Item
            name="giftMessage"
            label="Message (optional)"
            rules={[{ max: 500, message: 'Message must be 500 characters or fewer' }]}
          >
            <Input.TextArea maxLength={500} autoSize={{ minRows: 3, maxRows: 4 }} />
          </Form.Item>
        </Form>
      </Modal>
      <AppTour
        open={giftsTour.open}
        steps={giftsTourSteps}
        current={giftsTour.current}
        onChange={giftsTour.setCurrent}
        onClose={giftsTour.close}
        onFinish={giftsTour.close}
      />
    </div>
  );
};

export default AccountGifts;
